import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import type {
  NestedCellType,
  NestedOption,
  NestedTableColumn,
  NestedTableData,
  NestedTableLevelOverride,
  NestedTableRow,
} from '../../../components/nested-table/nested-table.types';
import {
  resolveLevel,
  resolveLevelColumns,
  rowsByLevel,
} from '../../../components/nested-table/nested-table.utils';
import { normalizeTableCellConfig, type TableCellButton, type TableCellConfig } from '../../../../chart-components/unified-table-matrix-chart/table-cell-config';
import { CellButtonsComponent, type CellButtonOptions } from './cell-buttons.component';
import { ColumnNavigationComponent } from './column-navigation.component';
import { TableCellConfigEditorComponent } from '../../../components/shared/table-cell-config-editor.component';

interface LevelView {
  index: number;
  title: string;
  columns: NestedTableColumn[];
  /** Field → distinct values at this level ("+ From data" under Value colours). */
  values: Record<string, string[]>;
}

export const NESTED_CELL_TYPES: ReadonlyArray<{ value: NestedCellType; label: string }> = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'percent', label: 'Percent' },
  { value: 'badge', label: 'Badge' },
  { value: 'index', label: 'Row number' },
  { value: 'input', label: 'Input (editable)' },
  { value: 'dropdown', label: 'Dropdown (editable)' },
];

/**
 * Per-level, per-column editor for the nested table. Lists the columns the
 * table ACTUALLY shows (derived from the data + config, via the same resolver
 * the component uses) and writes edits as `levelOverrides` — the config and
 * data underneath are never touched, so "Reset" is just dropping an override.
 */
@Component({
  selector: 'app-design-nested-table-columns',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatTooltipModule, TableCellConfigEditorComponent, ColumnNavigationComponent, CellButtonsComponent],
  templateUrl: './nested-table-columns.component.html',
  styleUrl: './nested-table-columns.component.scss',
})
export class NestedTableColumnsComponent {
  /** The widget data as rendered (config + rows). */
  readonly contextKey = input('');
  readonly data = input<Record<string, unknown> | null>(null);
  readonly overrides = input<NestedTableLevelOverride[]>([]);
  readonly overridesChange = output<NestedTableLevelOverride[]>();
  /** Option lists for the cell buttons' feature settings (null = the editor works them out). */
  readonly buttonContext = input<CellButtonOptions | null>(null);
  readonly tableName = input('');

  readonly activeLink = signal<{ context: string; level: number; field: string } | null>(null);
  expandedLinkField(level: number): string {
    const active = this.activeLink();
    return active?.context === this.contextKey() && active.level === level ? active.field : '';
  }
  expandLink(level: number, field: string): void { this.activeLink.set({ context: this.contextKey(), level, field }); }
  readonly cellTypes = NESTED_CELL_TYPES;
  private readonly openKeys = signal<ReadonlySet<string>>(new Set());

  readonly levels = computed<LevelView[]>(() => {
    const raw = this.data();
    if (!raw || !Array.isArray(raw['levels'])) return [];
    const config = { ...(raw as unknown as NestedTableData), levelOverrides: this.overrides() };
    const rows = (Array.isArray(config.staticData) ? config.staticData : config.rows ?? []) as NestedTableRow[];
    const byLevel = rowsByLevel(config, rows);
    return config.levels.map((_, index) => {
      const levelRows = byLevel[index] ?? [];
      const columns = resolveLevelColumns(config, index, levelRows);
      const values: Record<string, string[]> = {};
      columns.forEach((column) => {
        if (!column.field || column.type === 'number' || column.type === 'currency' || column.type === 'percent') return;
        const seen = new Set<string>();
        levelRows.forEach((row) => {
          const text = String(row?.[column.field] ?? '').trim();
          if (text) seen.add(text);
        });
        if (seen.size) values[column.field] = Array.from(seen);
      });
      return { index, title: resolveLevel(config, index)?.title ?? '', columns, values };
    });
  });

  isOpen(levelIndex: number, field: string): boolean {
    return this.openKeys().has(`${levelIndex}:${field}`);
  }

  toggleOpen(levelIndex: number, field: string): void {
    const key = `${levelIndex}:${field}`;
    this.openKeys.update((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }

  isOverridden(levelIndex: number, field: string): boolean {
    return !!this.overrides()[levelIndex]?.columns?.[field];
  }

  hasEditor(column: NestedTableColumn): boolean {
    return column.type === 'input' || column.type === 'dropdown';
  }

  /** The generic cell configuration (shared editor) of a column. */
  /** A column's buttons: with any, the column draws them instead of its value. */
  setCellButtons(levelIndex: number, column: NestedTableColumn, buttons: TableCellButton[]): void {
    const current = column.cellConfig ?? {};
    const display = buttons.length ? 'buttons' : current.display === 'buttons' ? undefined : current.display;
    this.setCellConfig(levelIndex, column.field, normalizeTableCellConfig({ ...current, buttons, display }));
  }
  setCellConfig(levelIndex: number, field: string, cellConfig: TableCellConfig | undefined): void {
    this.setColumn(levelIndex, field, { cellConfig });
  }
  /** Fields of a level, for rules that test another column (stable per level view). */
  levelFields(level: LevelView): string[] {
    return level.columns.map((column) => column.field);
  }

  optionsText(column: NestedTableColumn): string {
    return (column.options ?? [])
      .map((option) => (typeof option === 'string' ? option : (option as NestedOption).label))
      .join(', ');
  }

  // ── Writes ─────────────────────────────────────────────────────────────────

  setLevelTitle(levelIndex: number, title: string): void {
    this.patchLevel(levelIndex, (level) => ({ ...level, title }));
  }

  setColumn(levelIndex: number, field: string, patch: Partial<NestedTableColumn>): void {
    this.patchLevel(levelIndex, (level) => ({
      ...level,
      columns: { ...(level.columns ?? {}), [field]: { ...(level.columns?.[field] ?? {}), ...patch } },
    }));
  }

  setText(levelIndex: number, field: string, key: keyof NestedTableColumn, value: string): void {
    this.setColumn(levelIndex, field, { [key]: value === '' ? undefined : value } as Partial<NestedTableColumn>);
  }

  setNumber(levelIndex: number, field: string, key: keyof NestedTableColumn, value: string): void {
    const parsed = value === '' ? undefined : Number(value);
    this.setColumn(levelIndex, field, {
      [key]: parsed === undefined || Number.isFinite(parsed) ? parsed : undefined,
    } as Partial<NestedTableColumn>);
  }

  setOptions(levelIndex: number, field: string, text: string): void {
    const options = text.split(',').map((part) => part.trim()).filter(Boolean);
    this.setColumn(levelIndex, field, { options });
  }

  move(levelIndex: number, field: string, delta: -1 | 1): void {
    const fields = this.levels()[levelIndex]?.columns.map((column) => column.field) ?? [];
    const from = fields.indexOf(field);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= fields.length) return;
    [fields[from], fields[to]] = [fields[to], fields[from]];
    this.patchLevel(levelIndex, (level) => ({ ...level, order: fields }));
  }

  resetColumn(levelIndex: number, field: string): void {
    this.patchLevel(levelIndex, (level) => {
      const { [field]: _removed, ...rest } = level.columns ?? {};
      return { ...level, columns: rest };
    });
  }

  resetLevel(levelIndex: number): void {
    this.patchLevel(levelIndex, () => ({}));
  }

  trackLevel(_index: number, level: LevelView): number { return level.index; }
  trackColumn(_index: number, column: NestedTableColumn): string { return column.field; }

  private patchLevel(levelIndex: number, update: (level: NestedTableLevelOverride) => NestedTableLevelOverride): void {
    const next = [...this.overrides()];
    while (next.length <= levelIndex) next.push({});
    next[levelIndex] = update(next[levelIndex] ?? {});
    this.overridesChange.emit(next);
  }
}
